/* Scroll is the source of truth. Leaving the section never resets its timeline. */
(() => {
  'use strict';
  const controllers = new Map();
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  function mount(el) {
    if (controllers.has(el)) return;
    const video = el.querySelector('video');
    const stage = el.querySelector('.story-stage');
    const title = el.querySelector('[data-story-title]');
    if (!video || !stage || !title) return;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const portrait = matchMedia('(max-width: 650px)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const prefersLite = !!(connection && (connection.saveData || /(^|-)2g$|3g/.test(connection.effectiveType || '') || (connection.downlink > 0 && connection.downlink < 4)));
    let scenes = [];
    try { scenes = JSON.parse(el.querySelector('[data-story-scenes]')?.textContent || '[]'); } catch (_) {}
    const count = el.querySelector('[data-story-count]');
    const description = el.querySelector('[data-story-description]');
    const copy = el.querySelector('.story-copy');
    const steps = [...el.querySelectorAll('.story-steps > span')];
    let raf = 0, destroyed = false, started = false, inRange = true, sceneIndex = -1;
    let progress = 0, target = 0, requested = -1, requestedAt = 0, readyAt = 0;
    let loadAt = 0, hasFrame = false, lite = prefersLite, retryCount = 0;
    const listeners = [];
    const on = (node, event, fn, options) => {
      node.addEventListener(event, fn, options);
      listeners.push(() => node.removeEventListener(event, fn, options));
    };
    const duration = () => Number.isFinite(video.duration) && video.duration > 0 ? video.duration : 13.76375;
    const schedule = () => { if (!destroyed && !raf) raf = requestAnimationFrame(render); };
    function sourceURL() {
      const sources = [...video.querySelectorAll('source')];
      const source = sources.find(item => !item.media || matchMedia(item.media).matches);
      return source && ((lite && source.dataset.lite) || source.dataset.src || source.src);
    }
    function start(force = false) {
      if (destroyed || motion.matches || (started && !force)) return;
      const url = sourceURL();
      if (!url) return;
      started = true; requested = -1; requestedAt = 0;
      loadAt = performance.now(); hasFrame = false;
      video.classList.remove('is-ready', 'is-scrolling');
      video.muted = true; video.defaultMuted = true; video.preload = 'auto';
      video.src = url;
      video.load();
    }
    function reveal() {
      if (!motion.matches && video.readyState >= 2) {
        hasFrame = true; readyAt = performance.now();
        video.classList.add('is-ready', 'is-scrolling');
        el.classList.remove('story-media-unavailable');
      }
    }
    function seek() {
      if (!started || motion.matches || video.readyState < 1) return;
      const now = performance.now();
      const delta = Math.abs(video.currentTime - target);
      // Keep at most one ordinary seek in flight. Supersede a stale request
      // after a substantial jump/reversal instead of waiting for old network data.
      if (video.seeking) {
        if (now - requestedAt < 180 || Math.abs(requested - target) < 0.4) return;
      } else if (delta <= 1 / 48) {
        reveal(); return;
      }
      requested = target; requestedAt = now;
      try { video.currentTime = target; } catch (_) { requested = -1; }
    }
    function paintTitle(text, phrase) {
      title.replaceChildren();
      const at = phrase ? text.indexOf(phrase) : -1;
      if (at < 0) { title.textContent = text; return; }
      const accent = document.createElement('span');
      accent.className = 'zada-rose-keyword'; accent.textContent = phrase;
      title.append(document.createTextNode(text.slice(0, at)), accent,
        document.createTextNode(text.slice(at + phrase.length)));
    }
    function paintScene(time) {
      if (!scenes.length) return;
      let index = 0;
      for (let i = 0; i < scenes.length; i++) {
        if (time >= Number(scenes[i].at || 0)) index = i;
      }
      if (index === sceneIndex) return;
      sceneIndex = index;
      const scene = scenes[index];
      if (count) count.textContent = scene.label || '';
      paintTitle(String(scene.title || ''), scene.highlight || (index === 0 ? 'income of your own' : ''));
      if (description) description.textContent = scene.description || '';
      if (copy) {
        const last = index === scenes.length - 1;
        copy.classList.toggle('is-first', index === 0);
        copy.classList.toggle('is-final', last);
        const link = copy.querySelector('a');
        if (link) { link.href = '#enroll'; link.textContent = last ? 'Explore enrollment ↗' : 'Explore the course ↗'; }
      }
      steps.forEach((step, i) => step.classList.toggle('active', i === index));
    }
    function render() {
      raf = 0;
      if (destroyed) return;
      const rect = el.getBoundingClientRect();
      // Measure the sticky stage, not innerHeight: mobile browser bars change
      // innerHeight while the stage's stable viewport height remains fixed.
      const travel = Math.max(1, el.offsetHeight - stage.offsetHeight);
      progress = motion.matches ? 0 : clamp(-rect.top / travel, 0, 1);
      target = progress * Math.max(0, duration() - 1 / 24);
      el.style.setProperty('--story-progress', `${progress * 100}%`);
      paintScene(target);
      if (!started && rect.top < innerHeight + 800 && rect.bottom > -800) start();
      seek();
    }
    function fallback() {
      const previousURL = sourceURL();
      if (!lite) {
        lite = true;
        if (sourceURL() !== previousURL) { start(true); schedule(); return; }
      }
      if (video.error) el.classList.add('story-media-unavailable');
      // Retain the section's geometry and timeline so network recovery and
      // returning from below never turn the story into a reset/static section.
    }
    function mediaReady() { schedule(); }
    function seeked() { reveal(); schedule(); }
    function retry() {
      if (started && video.error && retryCount < 2) { retryCount++; start(true); }
      schedule();
    }
    function motionChanged() {
      if (motion.matches) {
        video.pause();
        video.classList.remove('is-ready', 'is-scrolling');
      } else { start(); }
      schedule();
    }
    on(window, 'scroll', schedule, {passive:true});
    on(window, 'resize', schedule, {passive:true});
    on(window, 'pageshow', retry);
    on(window, 'online', retry);
    on(document, 'visibilitychange', () => { if (!document.hidden) retry(); });
    on(motion, 'change', motionChanged);
    on(portrait, 'change', () => { if (started && !motion.matches) start(true); schedule(); });
    ['loadedmetadata', 'loadeddata', 'canplay', 'progress'].forEach(event => on(video, event, mediaReady));
    on(video, 'seeked', seeked);
    on(video, 'error', fallback);
    const observer = new IntersectionObserver(entries => {
      inRange = entries.some(entry => entry.isIntersecting);
      if (inRange) { if (!started) start(); schedule(); }
    }, {rootMargin:'800px'});
    observer.observe(el);
    // A blocked/slow high-quality source gets the smaller encode. It does not
    // switch back mid-scroll, which would flush the decoded frames again.
    const watchdog = setInterval(() => {
      if (destroyed || document.hidden || motion.matches || !inRange || !started) return;
      if (!lite && ((!hasFrame && performance.now() - loadAt > 3500) ||
          (hasFrame && video.seeking && performance.now() - readyAt > 4000))) fallback();
      if (!video.seeking && Math.abs(video.currentTime - target) > 1 / 48) schedule();
    }, 500);
    controllers.set(el, () => {
      destroyed = true; cancelAnimationFrame(raf); clearInterval(watchdog);
      observer.disconnect(); listeners.forEach(remove => remove());
      video.pause(); video.removeAttribute('src'); video.load();
    });
    render();
  }
  function init(root = document) {
    if (root.matches?.('[data-story-scrub]')) mount(root);
    root.querySelectorAll('[data-story-scrub]').forEach(mount);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), {once:true});
  else init();
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [el, cleanup] of controllers) {
      if (event.target === el || event.target.contains(el)) { cleanup(); controllers.delete(el); }
    }
  });
})();
